/**
 * CriticalZonesWidget — Collapsible panel showing top districts by open high-priority incidents.
 * Clicking a zone filters the task list by that district.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, MapPin } from 'lucide-react';
import api from '../lib/api';

const SEVERITY_CONFIG = {
  critical: { dot: 'bg-red-500 animate-pulse', badge: 'bg-red-100 text-red-700', label: 'Crítica' },
  high:     { dot: 'bg-orange-500',             badge: 'bg-orange-100 text-orange-700', label: 'Alta' },
  medium:   { dot: 'bg-yellow-400',             badge: 'bg-yellow-100 text-yellow-700', label: 'Media' },
};

export default function CriticalZonesWidget({ onZoneClick }) {
  const [collapsed, setCollapsed] = useState(false);

  const { data } = useQuery({
    queryKey: ['critical-zones'],
    queryFn: () => api.get('/analytics/critical-zones').then(r => r.data),
    refetchInterval: 2 * 60 * 1000,
    staleTime: 60 * 1000,
  });

  const zones = data?.zones || [];
  const totalOpen = data?.total_open ?? 0;
  const topSeverity = zones[0]?.severity || 'medium';
  const cfg = SEVERITY_CONFIG[topSeverity] || SEVERITY_CONFIG.medium;

  if (!zones.length) return null;

  return (
    <div className="border-b border-border bg-white dark:bg-gray-900">
      {/* Header */}
      <button
        onClick={() => setCollapsed(c => !c)}
        data-testid="critical-zones-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
          <span>Zonas Críticas</span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cfg.badge}`}>
            {zones[0]?.total_open} incid.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground font-normal">
            {totalOpen.toLocaleString('es-ES')} abiertas total
          </span>
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${collapsed ? '-rotate-90' : ''}`} />
        </div>
      </button>

      {/* Zones list */}
      {!collapsed && (
        <div className="px-3 pb-3 space-y-1" data-testid="critical-zones-list">
          {zones.map((zone, idx) => {
            const c = SEVERITY_CONFIG[zone.severity] || SEVERITY_CONFIG.medium;
            const pct = totalOpen > 0 ? Math.round((zone.total_open / totalOpen) * 100) : 0;
            return (
              <button
                key={zone.district}
                onClick={() => onZoneClick(zone.district)}
                data-testid={`zone-${zone.district.replace(/\s+/g, '-').toLowerCase()}`}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-muted/40 cursor-pointer group text-left transition-colors"
              >
                {/* Rank */}
                <span className="text-[10px] text-muted-foreground w-3 flex-shrink-0 font-mono">
                  {idx + 1}
                </span>
                {/* Severity dot */}
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${c.dot}`} />
                {/* District name + bar */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-medium truncate group-hover:text-primary transition-colors">
                      {zone.district}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ml-1 ${c.badge}`}>
                      {zone.total_open}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${zone.severity === 'critical' ? 'bg-red-500' : zone.severity === 'high' ? 'bg-orange-400' : 'bg-yellow-400'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {zone.total_open} abiertas ({pct}%)
                  </span>
                </div>
                {/* Map pin hint */}
                <MapPin className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity" />
              </button>
            );
          })}
          <p className="text-[10px] text-muted-foreground px-2 pt-1">
            Clic en una zona para filtrar la lista
          </p>
        </div>
      )}
    </div>
  );
}
